/* eslint-disable react/prop-types */ // TODO: add props
import { Link } from 'react-router-dom';
import Immutable from 'immutable';
import cx from 'classnames';
import { SELF_RATING_MAX } from 'edrolo/domains/lessons';
import ProgressBar from '../ProgressBar';
import { groupByLastFlag } from 'core/utils';
import ChunkProgressIndicator from '../ChunkProgressIndicator';
import styles from './StudentLessonProgress.scss';
import { Table } from 'edrolo/domains/teacherResultsTable';
import { constants as analyticsConstants } from 'edrolo/domains/studentProgressGrid/analytics';

const LinkCell = ({ navigatedFrom, to, className, onClick, ...props }) => {
  const toLocation = {
    pathname: to,
    state: { navigatedFrom },
  };

  return (
    <Link to={toLocation} className={className} onClick={onClick} {...props} />
  );
};

const StudentLessonProgress = ({
  progress,
  chunks,
  totalQuizMarks,
  buildChunkUrl,
  buildStudentCourseProgressUrl,
  buildStudentNotebookUrl,
  navigatedFrom,
  analyticsPayload,
  studentProgressSuccessMeasureResponses,
  shouldShowSelfRating,
  shouldShowStudentNotes,
}) => {
  const studentName = progress.getIn(['profile', 'name'], '');
  const profileId = progress.getIn(['profile', 'id']);
  const studentChunkViews = progress.getIn(
    ['progress', 'visited_chunks'],
    Immutable.List()
  );
  const studentQuizResults = progress.getIn(
    ['progress', 'quiz_results'],
    Immutable.Map()
  );
  const lessonProgressSummary = progress.get('summary');
  const groupedChunks = groupByLastFlag(chunks);

  // we're working with *grouped* chunks so we can apply the correct flags for first|middle|last..
  const chunkProgressIndicators = groupedChunks.reduce(
    (result, groupChunks) => {
      // render this group of chunks
      const renderedGroup = groupChunks.map((chunk, index) => {
        const chunkQuestion = chunk.get('question');
        const chunkActivity = chunk.get('activity');
        const chunkQuizResults =
          chunkQuestion &&
          studentQuizResults &&
          studentQuizResults.get(chunkQuestion.get('id').toString());

        // work out where we are in the group (only care if group > 1)
        const situ = {};
        if (groupChunks.size > 1) {
          situ.grouped = true;
          situ.first = index === 0;
          situ.last = index === groupChunks.size - 1;
        }

        // Don't show results info for review questions (but still show question dot)
        const showResult =
          chunkQuizResults && !chunkQuestion.get('question_to_review');

        const hasSuccessMeasure = chunk.get('success_measure');
        const successMeasureResponseValue =
          studentProgressSuccessMeasureResponses
            ?.find(
              (record) =>
                record.get('profile_id') == profileId &&
                record.get('success_measure_id') ==
                  chunk.getIn(['success_measure', 'id'])
            )
            ?.get('value', '');

        return (
          <ChunkProgressIndicator
            key={chunk.get('id')}
            chunkUrl={buildChunkUrl(chunk.get('index'))}
            viewed={
              studentChunkViews && studentChunkViews.includes(chunk.get('id'))
            }
            hasActivity={!!chunkActivity}
            hasQuestion={!!chunkQuestion}
            hasResult={showResult}
            marksAvailable={
              chunkQuestion && chunkQuestion.get('marks_available')
            }
            marksObtained={
              chunkQuizResults && chunkQuizResults.get('marks_obtained')
            }
            chunkTitle={chunk.get('title')}
            successMeasureResponseValue={successMeasureResponseValue}
            hasSuccessMeasure={hasSuccessMeasure}
            isVeryFirstChunk={chunk.get('index') === 1}
            isVeryLastChunk={chunk.get('index') === chunks.size}
            shouldShowSelfRating={shouldShowSelfRating}
            {...situ}
          />
        );
      });

      // concatenate back together to build the full list of chunks again
      return result.concat(renderedGroup);
    },
    Immutable.List()
  );

  const quizResultsTallyPercentage =
    lessonProgressSummary.get('quizResultsTally') && totalQuizMarks
      ? Math.round10(
          (lessonProgressSummary.get('quizResultsTally') / totalQuizMarks) * 100
        )
      : 0;

  return (
    <Table.Row data-test="student-lesson-progress-row">
      <Table.RowHeader className={styles.nameCell} isSticky>
        <LinkCell
          navigatedFrom={navigatedFrom}
          to={buildStudentCourseProgressUrl(profileId)}
          className={styles.studentNameLink}
          onClick={() => {
            global.analytics.track(
              analyticsConstants.events.CLICKED_LINK_TO_STUDENT_COURSE_PROGRESS,
              {
                origin: analyticsConstants.origin.CLASS_STUDENT_LESSON_PROGRESS,
                page: analyticsConstants.pages.STUDENT_LESSON_PROGRESS,
                class_id: analyticsPayload.classId,
                student_user_id: analyticsPayload.studentUserId,
              }
            );
          }}
        >
          <span data-mp-mask>{studentName}</span>
        </LinkCell>
      </Table.RowHeader>
      <Table.Cell className={styles.progressCell}>
        <ProgressBar
          value={lessonProgressSummary.get('visitedChunks').size}
          maxValue={chunks.size}
          small
          showIndicator={false}
        />
      </Table.Cell>
      <Table.Cell className={styles.progressCell}>
        <ProgressBar
          value={quizResultsTallyPercentage}
          small
          showIndicator={false}
          dashForZero={!totalQuizMarks}
        />
      </Table.Cell>
      {shouldShowSelfRating && (
        <Table.Cell className={styles.progressCell}>
          <ProgressBar
            value={lessonProgressSummary.get('selfRating')}
            maxValue={SELF_RATING_MAX}
            small
          />
        </Table.Cell>
      )}
      {shouldShowStudentNotes && (
        <Table.Cell className={styles.studentNotebookLinkCell}>
          <LinkCell
            navigatedFrom={navigatedFrom}
            to={buildStudentNotebookUrl(profileId)}
            className={cx(
              styles.studentNotebookLink,
              lessonProgressSummary.get('noteTaken')
                ? styles.activeStudentNotebookLink
                : styles.inactiveStudentNotebookLink
            )}
            onClick={() => {
              global.analytics.track(
                analyticsConstants.events.CLICKED_LINK_TO_STUDENT_NOTEBOOK,
                {
                  origin:
                    analyticsConstants.origin.CLASS_STUDENT_LESSON_PROGRESS,
                  page: analyticsConstants.pages.STUDENT_LESSON_PROGRESS,
                  class_id: analyticsPayload.classId,
                  lesson_id: analyticsPayload.lessonId,
                  student_user_id: analyticsPayload.studentUserId,
                }
              );
            }}
          >
            See notes
          </LinkCell>
        </Table.Cell>
      )}
      {chunkProgressIndicators.map((viewedIndicator, i) => (
        <Table.Cell className={styles.chunkCell} key={i}>
          {viewedIndicator}
        </Table.Cell>
      ))}
    </Table.Row>
  );
};

StudentLessonProgress.defaultProps = {
  buildChunkUrl: (x) => x,
};

export default StudentLessonProgress;
